import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  effect,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  CartDataService,
  CartRelationshipService,
  CartService,
  ConfigurationService,
  HREF,
  isAppleBrand,
  OrderLimitService,
  StockLimitService,
  outOfStockItemsFrom,
  OverlayService,
  ProcessedProduct,
  ProductAppleCareService,
  ProductCapabilitiesService,
  ProductProcessingService,
  ProductService,
  UserService,
} from '@core';
import { NgxSkeletonLoaderConfigTheme, NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, filter, finalize, switchMap, tap, catchError, map } from 'rxjs/operators';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject, Subject, EMPTY, forkJoin, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from '..';
import { orderLimitTileMessage } from '../constants/order-limit-messages';
import { STOCK_MESSAGES } from '../constants/stock-messages';
import { CartItemComponent } from '../cart-item/cart-item.component';
import { AccessibleClickDirective } from '../directives/accessible-click.directive';
import { DigitsGroupingPipe } from '../pipes/digits-grouping.pipe';
import { CdkTrapFocus } from '@angular/cdk/a11y';

import { AppleCareItem } from '../../../../ui/apple-components/apple-care-listing-product/apple-care-listing-product.component';
import { ProductCart, ProductDetailsCart } from '@models';
import { ApplecareModalComponent } from 'libs/ui/apple-components/applecare-modal/applecare-modal.component';

@Component({
  selector: 'lib-cart-list',
  templateUrl: './cart-list.component.html',
  styleUrl: './cart-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CartItemComponent,
    MatButton,
    NgxSkeletonLoaderModule,
    MatIconModule,
    AccessibleClickDirective,
    DigitsGroupingPipe,
    CdkTrapFocus,
    ApplecareModalComponent,
  ],
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class CartListComponent implements OnInit {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  private readonly cartDataService = inject(CartDataService);
  private readonly overlayService = inject(OverlayService);
  private readonly router = inject(Router);
  private readonly bannerService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly capabilitiesService = inject(ProductCapabilitiesService);
  private readonly cartService = inject(CartService);
  private readonly productService = inject(ProductService);
  private readonly processingService = inject(ProductProcessingService);
  private readonly productApplecareService = inject(ProductAppleCareService);
  private readonly cartRelationshipService = inject(CartRelationshipService);
  private readonly orderLimitService = inject(OrderLimitService);
  private readonly stockLimitService = inject(StockLimitService);
  userService = inject(UserService);

  loading = signal(false);
  private triggerFetch = new Subject<boolean>();
  autoFocusActions = false;
  isProcessingAppleCare = signal(false);
  readonly checkoutLoading = signal(false);

  readonly skeletonConfig: NgxSkeletonLoaderConfigTheme = {
    height: '144px',
    width: '100%',
    border: '1px solid white',
  };

  readonly appleCareSkeletonConfig: NgxSkeletonLoaderConfigTheme = {
    height: '60px',
    width: '100%',
    border: '1px solid #f0f0f0',
    borderRadius: '8px',
    marginTop: '8px',
  };

  readonly updatedProductQuantity$ = new BehaviorSubject<{
    productId: number;
    quantity: number;
    lineNumber?: number;
  } | null>(null);

  readonly vm = computed(() => ({
    productCard: this.cartDataService.productDetailsCart(),
    loading: this.loading(),
    isEmpty: !this.cartDataService.productDetailsCart()?.products?.length,
  }));

  processedProducts = computed<ProcessedProduct[]>(() => {
    const products = this.vm().productCard?.products ?? [];
    return this.processingService.createProcessedProducts(products);
  });

  filteredProducts = signal<ProcessedProduct[]>([]);
  capabilitiesLoading = signal(false);

  constructor() {
    effect(() => {
      const products = this.vm().productCard?.products ?? [];
      if (products.length > 0) {
        this.loadProductCapabilities();
      }
    });

    effect(() => {
      const appleCareMap = this.capabilitiesService.appleCareItemsMap();
      const engravableMap = this.capabilitiesService.engravableProductMap();
      const cartProducts = this.vm().productCard?.products ?? [];

      if (cartProducts.length > 0 && (appleCareMap.size > 0 || engravableMap.size > 0)) {
        this.capabilitiesLoading.set(false);
      }
    });

    effect(() => {
      const processed = this.processedProducts();
      if (processed.length > 0) {
        this.processingService.filterOutAppleCareProducts(processed).subscribe((filtered) => {
          this.filteredProducts.set(filtered);
        });
      } else {
        this.filteredProducts.set([]);
      }
    });
  }

  ngOnInit(): void {
    this.bindFetchProductsCallToHttpRequest();
    this.detectUpdatedProductQuantity();
  }

  loadProductCapabilities(): void {
    const cartProducts = this.vm().productCard?.products ?? [];
    if (cartProducts.length === 0) return;
    this.capabilitiesLoading.set(true);
    this.capabilitiesService.loadProductCapabilities(cartProducts, true);
  }

  private bindFetchProductsCallToHttpRequest(): void {
    this.triggerFetch
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(200),
        tap((showLoader) => {
          if (showLoader) {
            this.loading.set(true);
          }
        }),
        switchMap(() => this.cartDataService.fetchProductCart().pipe(finalize(() => this.loading.set(false)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private fetchProducts(showLoader = true): void {
    this.triggerFetch.next(showLoader);
  }

  onUpdate(value: { productId: number; quantity: number; lineNumber?: number }): void {
    this.updatedProductQuantity$.next(value);
  }

  /**
   * SIMPLIFIED: Remove product and its 1:1 linked AppleCare
   */
  onRemove({ productId, lineNumber }: { productId: number; lineNumber?: number }): void {
    // RCP-452 — a SKU can span several lines (pre-RCP-379 Apple carts): remove the clicked one.
    const product = this.vm().productCard?.products?.find(
      (p) => p.productId === productId && (lineNumber === undefined || p.lineNumber === lineNumber),
    );
    if (!product) return;

    this.cartDataService
      .removeProduct(productId, product.lineNumber) // Pass lineNumber
      .pipe(
        tap(() => {
          this.cartDataService.optimisticProductRemove(productId, product.lineNumber);
        }),
        switchMap(() => this.cartDataService.fetchProductCart()),
        // After cart refresh, validateAppleCareProducts will identify and remove any orphaned AppleCare items
        // If backend cascade-deleted them, they won't be found. If not, orphan handler will clean them up.
        tap(() => {
          this.validateAppleCareProducts();
        }),
        catchError((err: HttpErrorResponse) => {
          // If 404, the item is already removed - update UI optimistically and refresh cart
          if (err?.status === 404) {
            this.cartDataService.optimisticProductRemove(productId, product.lineNumber);
            return this.cartDataService.fetchProductCart().pipe(
              tap(() => {
                this.validateAppleCareProducts();
              }),
            );
          }
          // For other errors, log and try to refresh cart to sync state
          console.error('Failed to remove product', err);
          return this.cartDataService.fetchProductCart().pipe(
            tap(() => {
              this.validateAppleCareProducts();
            }),
            catchError(() => EMPTY), // If refresh also fails, just continue silently
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onAddAppleCare({
    appleCareItem,
    parentLineNumber,
  }: {
    appleCareItem: AppleCareItem;
    parentLineNumber: number;
  }): void {
    const parentProduct = this.vm().productCard?.products?.find((p) => p.lineNumber === parentLineNumber);

    // Validate parent product exists in cart AND has valid lineNumber
    if (!parentProduct) {
      console.error('Cannot add AppleCare: Parent product not found at lineNumber:', parentLineNumber);
      return;
    }

    // Validate that parent product has a valid positive lineNumber
    if (!parentProduct.lineNumber || parentProduct.lineNumber <= 0) {
      console.error('Cannot add AppleCare: Parent product has invalid lineNumber', {
        parentProduct,
        lineNumber: parentProduct.lineNumber,
        parentLineNumber,
      });
      return;
    }

    this.isProcessingAppleCare.set(true);

    this.productService
      .getProductByProdID(appleCareItem.id.toString())
      .pipe(
        switchMap((appleCareProductDetails) =>
          this.cartService.addCartProductSuppressDropdown(
            appleCareProductDetails,
            { autoFocusActions: false },
            null,
            parentProduct.lineNumber, // Use the validated lineNumber
          ),
        ),
        finalize(() => {
          this.isProcessingAppleCare.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (err) => {
          console.error('Failed to add AppleCare', err);
        },
      });
  }
  onRemoveAppleCare({
    appleCareItem,
    parentLineNumber,
  }: {
    appleCareItem: AppleCareItem;
    parentLineNumber: number;
  }): void {
    // Find AppleCare by productId and parent's relatedLineNumber to avoid stale lineNumber issues
    const appleCareProductInCart = this.vm().productCard?.products?.find(
      (p) => p.productId === appleCareItem.productId && p.relatedLineNumber === parentLineNumber,
    );

    if (!appleCareProductInCart || !appleCareProductInCart.lineNumber) {
      console.error('AppleCare product not found in cart', {
        appleCareItem,
        parentLineNumber,
        cart: this.vm().productCard,
      });
      return;
    }

    this.cartDataService
      .removeProduct(appleCareProductInCart.productId, appleCareProductInCart.lineNumber)
      .pipe(
        tap(() => {
          this.cartDataService.optimisticProductRemove(
            appleCareProductInCart.productId,
            appleCareProductInCart.lineNumber,
          );

          // Update capabilities map using the passed parentLineNumber
          const updatedMap = new Map(this.capabilitiesService.appleCareItemsMap());
          const updatedPresenceMap = new Map(this.capabilitiesService.appleCarePresenceMap());

          updatedMap.delete(parentLineNumber);
          updatedPresenceMap.set(parentLineNumber, false);

          this.capabilitiesService.appleCareItemsMap.set(updatedMap);
          this.capabilitiesService.appleCarePresenceMap.set(updatedPresenceMap);
        }),
        switchMap(() => this.cartDataService.fetchProductCart()),
        tap(() => {
          this.validateAppleCareProducts();
        }),
        catchError((err) => {
          // If 404, the item is already removed
          if (err?.status === 404) {
            this.cartDataService.optimisticProductRemove(
              appleCareProductInCart.productId,
              appleCareProductInCart.lineNumber,
            );
            const updatedMap = new Map(this.capabilitiesService.appleCareItemsMap());
            const updatedPresenceMap = new Map(this.capabilitiesService.appleCarePresenceMap());
            updatedMap.delete(parentLineNumber);
            updatedPresenceMap.set(parentLineNumber, false);
            this.capabilitiesService.appleCareItemsMap.set(updatedMap);
            this.capabilitiesService.appleCarePresenceMap.set(updatedPresenceMap);
            return this.cartDataService.fetchProductCart();
          }
          // For other errors, log and continue
          console.error('Failed to remove AppleCare', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  isAppleProduct(product: ProcessedProduct): boolean {
    return isAppleBrand(product);
  }

  onClose(): void {
    this.overlayService.close();
  }

  redirectToCart(): void {
    this.router.navigate([HREF.CART]);
    this.onClose();
  }

  redirectToCheckout(): void {
    if (this.checkoutLoading()) return;

    const cart = this.vm().productCard;
    // RID-1286 — the cart can be rendered empty (it still carries the auto-removal message).
    if (!cart?.products?.length) {
      this.bannerService.error('Error', 'Your cart is empty.');
      return;
    }

    const isAllProductsValid = cart.products?.every((product) => product.isAvailable);
    if (!isAllProductsValid) {
      this.bannerService.error(
        'Error',
        'You have one or more items in your cart that is no longer available. Please remove it to continue to checkout.',
      );
      return;
    }

    // FCR 19.1 AC04/AC06/AC07 — run the checkout gate: it re-reads the cart and, only when the
    // monthly limit is really exceeded, applies the auto-reduce/remove adjustment.
    this.checkoutLoading.set(true);
    this.orderLimitService
      .resolveCheckoutGate()
      .pipe(
        finalize(() => this.checkoutLoading.set(false)),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ adjusted, stockBlocked, cart: freshCart }) => {
        // RCP-458 DoD11/DoD12 — a line is above the available count: name the products and send the
        // customer to the Cart, where the tile messages show what to change.
        if (stockBlocked) {
          this.stockLimitService.notifyCheckoutBlocked(this.stockLimitService.stockIssues(freshCart));
          this.redirectToCart();
          return;
        }

        // Something was really reduced/removed — send the customer to the Cart (not Checkout), where
        // the top banner and the under-"Your items" stack explain what changed (AC06/AC07/AC13).
        if (adjusted) {
          this.redirectToCart();
          return;
        }

        // RID-1292 — nothing was taken away (the cart fits the remaining allowance, or the gate flag
        // was stale): continue to Checkout with the freshly read cart instead of bouncing to the Cart.
        if (!freshCart?.products?.length) {
          this.bannerService.error('Error', 'Your cart is empty.');
          this.redirectToCart();
          return;
        }

        this.proceedToCheckout(freshCart);
      });
  }

  /** AppleCare relationship validation + navigation — the single exit to Checkout. */
  private proceedToCheckout(cart: ProductDetailsCart): void {
    this.productApplecareService
      .isAppleCareProduct()
      .pipe(
        map(
          (appleCareProductIds) =>
            this.cartRelationshipService.validateAppleCareRelatedLineNumbers(cart, appleCareProductIds).isValid,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isValid) => {
        if (!isValid) {
          this.bannerService.error(
            'Invalid Cart',
            'One or more AppleCare items in your cart have invalid associations. Please review your cart before checkout.',
          );
          return;
        }

        this.router.navigate([HREF.CHECKOUT]);
        this.onClose();
      });
  }

  redirectToHome(): void {
    this.router.navigate([HREF.HOME]);
    this.onClose();
  }

  /**
   * RID-1278 — cap the "+" for this product: the line reached the remaining monthly allowance
   * for the SKU, OR the product was auto-reduced at checkout (AC07a — stays capped after the reduce).
   * RID-1284 — Apple carts saved before RCP-379 can still spread a SKU over several lines, so the
   * service compares the per-SKU total for those.
   */
  isProductLimitReached(product: ProductCart): boolean {
    return this.orderLimitService.isTileLimitReached(product);
  }

  /**
   * RID-1278 — upper bound for the stepper: the remaining monthly allowance for this SKU,
   * RCP-458 — capped further by the SKU's available count.
   */
  productMaxQuantity(product: ProductCart): number {
    return Math.min(
      this.orderLimitService.remainingFor(product.productId),
      this.stockLimitService.capFor(product) ?? Infinity,
    );
  }

  /**
   * RCP-458 DoD11 — the line is above the available count; only the first tile of the SKU says so
   * (the backend flags every line of it). An unavailable product already has its own message.
   */
  productStockMessage(product: ProductCart, index: number): string | undefined {
    if (!product.exceedsAvailableStock || !product.isAvailable) return undefined;
    const firstIndex = this.filteredProducts().findIndex((line) => line.productId === product.productId);
    return firstIndex === index ? STOCK_MESSAGES.tileExceeds : undefined;
  }

  /**
   * FCR 19.1 — per-tile limit message once the cap is reached, else none (AC03 — nothing before
   * the cap). The mini cart has no banner stack, so this message stays even after an auto-reduce.
   */
  productLimitMessage(product: ProductCart): string | undefined {
    return orderLimitTileMessage(this.isProductLimitReached(product), this.orderLimitService.skuLimit());
  }

  /**
   * SIMPLIFIED: No AppleCare quantity adjustment in detectUpdatedProductQuantity
   */
  private detectUpdatedProductQuantity(): void {
    this.updatedProductQuantity$
      .pipe(
        filter((data): data is { productId: number; quantity: number; lineNumber?: number } => !!data),
        tap(() => this.loading.set(true)),
        switchMap(({ productId, quantity, lineNumber }) => {
          return this.cartDataService.updateProduct({ productId, quantity, lineNumber }).pipe(
            tap(() => this.cartDataService.optimisticProductUpdate(productId, quantity, lineNumber)),
            tap(() => this.fetchProducts(false)),
            // RCP-458 DoD10 — caught inside the switchMap so a refused raise (409) doesn't end the
            // stream; the refetch re-syncs the stepper and clears `loading`.
            catchError((error) => {
              const items = outOfStockItemsFrom(error);
              if (items) {
                this.stockLimitService.notifyOutOfStock(items);
              } else {
                console.error('Failed to update product quantity', error);
              }
              this.fetchProducts(false);
              return EMPTY;
            }),
            // REMOVED: adjustLinkedAppleCareQuantities - no quantity sync needed
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private validateAppleCareProducts(): void {
    const cart = this.vm().productCard;
    if (!cart) return;

    this.productApplecareService
      .isAppleCareProduct()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appleCareProductIds) => {
        const orphanedItems = this.cartRelationshipService.validateAppleCareProducts(cart, appleCareProductIds);

        if (orphanedItems.length > 0) {
          this.removeOrphanedAppleCareItems(orphanedItems);
        }
      });
  }

  private removeOrphanedAppleCareItems(orphanedItems: ProductCart[]): void {
    if (orphanedItems.length === 0) return;

    // Filter to only items that still exist in the current cart state
    // Backend may have already cascade-deleted some of them
    const currentCart = this.vm().productCard;
    const itemsToRemove = orphanedItems.filter((item) => {
      if (!currentCart?.products) return false;
      const lineNumber =
        item.lineNumber && this.cartRelationshipService.isValidLineNumber(item.lineNumber)
          ? item.lineNumber
          : undefined;

      // Check if item still exists in cart
      return currentCart.products.some(
        (p) => p.productId === item.productId && (lineNumber ? p.lineNumber === lineNumber : true),
      );
    });

    if (itemsToRemove.length === 0) {
      // All items were already removed by backend cascade delete
      return;
    }

    // Create individual removal observables for each orphaned item that still exists
    const removalObservables = itemsToRemove.map((item) => {
      const lineNumber =
        item.lineNumber && this.cartRelationshipService.isValidLineNumber(item.lineNumber)
          ? item.lineNumber
          : undefined;

      return this.cartDataService.removeProduct(item.productId, lineNumber).pipe(
        tap(() => this.cartDataService.optimisticProductRemove(item.productId, lineNumber)),
        catchError((err: HttpErrorResponse) => {
          // 404 means backend already removed it (cascade delete) - treat as success, no logging
          if (err?.status === 404) {
            this.cartDataService.optimisticProductRemove(item.productId, lineNumber);
            return of(null);
          }
          // For other errors, log but continue - optimistically remove from UI
          console.error(`Failed to remove orphaned AppleCare item ${item.productId}`, err);
          this.cartDataService.optimisticProductRemove(item.productId, lineNumber);
          return of(null);
        }),
      );
    });

    // Wait for all removals to complete, then refresh cart once
    forkJoin(removalObservables)
      .pipe(
        tap(() => {
          // Refresh cart once after all removals complete
          this.fetchProducts(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        complete: () => {
          // Show banner after all removals are processed
          this.bannerService.alert(
            'Cart Updated',
            `Removed ${itemsToRemove.length} AppleCare item${itemsToRemove.length > 1 ? 's' : ''} that no longer had associated products.`,
          );
        },
      });
  }

  onEscapeKey(): void {
    this.onClose();
  }
}
